import { ViewModelStore } from '../../state';
import type { OnDestroy, OnInit } from '@angular/core';
import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import type { ViewDidEnter } from '@ionic/angular';
import {
  AlertController,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonItemGroup,
  IonLabel,
  IonList,
  IonListHeader,
  IonText,
  IonTitle,
  IonToolbar,
  NavController,
  Platform,
} from '@ionic/angular';
import { HelperService } from '@rdlabo/shared/src/lib/services/helper.service';
import { AuthService } from '@rdlabo/shared/src/auth/auth.service';
import {
  KitStorageService,
  KitOverlayController,
  kitClearStoragePreservingKeys,
  KIT_LAST_AUTH_EMAIL_KEY,
} from '@rdlabo/ionic-angular-kit';
import { transactionDefault } from '../../state';
import type { IDatabase } from '../../config/constant';
import { SlipType } from '../../config/constant';
import packageInfo from '../../../../../../package.json';
import { Geolocation } from '@capacitor/geolocation';
import { App } from '@capacitor/app';
import { filter, firstValueFrom, Subscription } from 'rxjs';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { environment } from '@rdlabo/shared/src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { KeyValuePipe } from '@angular/common';
import { FirebaseAnalytics } from '@capacitor-firebase/analytics';
import type { PreTransactionKind } from '@rdlabo/odss-mobile-hono/src/api/functions/transaction/api';

@Component({
  selector: 'app-home',
  templateUrl: './home.page.html',
  styleUrls: ['./home.page.scss'],
  imports: [
    RouterLink,
    KeyValuePipe,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonText,
    IonContent,
    IonList,
    IonListHeader,
    IonLabel,
    IonItem,
    IonIcon,
    IonItemGroup,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HomePage implements OnInit, OnDestroy, ViewDidEnter {
  readonly vm = new ViewModel(this);
  readonly auth = inject(AuthService);
  readonly platform = inject(Platform);
  readonly #helper = inject(HelperService);
  readonly #router = inject(Router);
  readonly #http = inject(HttpClient);

  readonly slipType = SlipType;
  readonly appVersion = packageInfo.version;
  readonly nativeVersion = signal<string | null>(null);
  readonly titleEnum = {
    odss: '小田商店',
    futaba: 'フタバ水栓',
  };
  readonly initSubscription$ = new Subscription();

  readonly title = signal<string>(this.titleEnum.odss);
  readonly emailUser = signal<string>('');

  constructor() {}

  async ngOnInit() {
    if (this.platform.is('hybrid')) {
      const { version } = await App.getInfo();
      this.nativeVersion.set(version);
    }

    this.initSubscription$.add(
      this.auth.getState$().subscribe((data) => {
        this.emailUser.set(data?.email?.split('@')[0]);
      }),
    );

    this.initSubscription$.add(
      this.#router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(async (params: any) => {
        firstValueFrom(
          this.#http.post<void>(environment.api() + 'analytics', {
            route: params.url,
          }),
        ).catch();

        if (this.platform.is('hybrid')) {
          FirebaseAnalytics.logEvent({
            name: params.url,
            params: {
              url: params.url,
            },
          }).catch();
        }
      }),
    );
  }

  async ngOnDestroy() {
    this.initSubscription$.unsubscribe();
  }

  ionViewDidEnter() {
    if (!Object.hasOwn(localStorage, 'apiKey') || !localStorage.getItem('apiKey')) {
      this.vm.doSignout(true).catch();
    } else {
      this.title.set(this.titleEnum[localStorage.getItem('apiKey') as IDatabase]);
    }

    Geolocation?.getCurrentPosition()
      .then((data) => (this.#helper.position = data))
      .catch(() => undefined);
  }

  readonly unsorted = (_a, _b) => 1;
}

class ViewModel extends ViewModelStore<HomePage> {
  readonly #helper = inject(HelperService);
  readonly #alertCtrl = inject(AlertController);
  readonly #storage = inject(KitStorageService);
  readonly #overlay = inject(KitOverlayController);
  readonly #navCtrl = inject(NavController);
  readonly #alertController = inject(AlertController);

  readonly #auth = inject(AuthService);

  async checkPosition() {
    const position = this.#helper.position || (await Geolocation?.getCurrentPosition());
    await this.#overlay.presentToast({
      message: `[${position.coords.latitude}/${position.coords.longitude}]`,
    });
  }

  async doTransaction(type: string): Promise<boolean> {
    const userName = await this.#helper.getUserName();

    this.setTransaction(
      Object.assign(transactionDefault(), {
        slipType: parseInt(type, 10),
        userName,
      }),
    );
    if ([3, 5, 12].includes(parseInt(type, 10))) {
      return this.#navCtrl.navigateForward('/transaction/pickup');
    } else {
      return this.#navCtrl.navigateForward('/transaction/choice/company');
    }
  }

  async doPreTransaction(kind: PreTransactionKind): Promise<boolean> {
    const userName = await this.#helper.getUserName();
    this.setTransaction({
      ...transactionDefault(),
      slipType: kind === 'estimate' ? 6 : 9,
      preTransactionKind: kind,
      userName,
    });
    return this.#navCtrl.navigateForward(
      kind === 'stock' ? '/pre-transaction/pickup' : '/pre-transaction/choice/company',
    );
  }
  doRestore() {
    this.#navCtrl.navigateForward('/transaction/restore');
  }

  async doSignout(force = false): Promise<void> {
    if (force === true) {
      localStorage.clear();
      await Promise.all([
        kitClearStoragePreservingKeys(this.#storage, [KIT_LAST_AUTH_EMAIL_KEY]),
        this.#auth.signOut(),
      ]);
      this.#navCtrl.navigateForward('/auth/signin');
      return;
    }
    const alert = await this.#alertController.create({
      header: '確認',
      message: 'サインアウトしてもよろしいですか？',
      buttons: [
        {
          text: 'いいえ',
        },
        {
          text: 'はい',
          handler: async () => {
            localStorage.clear();
            await kitClearStoragePreservingKeys(this.#storage, [KIT_LAST_AUTH_EMAIL_KEY]);
            await this.#auth.signOut();
            this.#navCtrl.navigateForward('/auth/signin');
          },
        },
      ],
    });
    alert.present();
  }

  async doDataDeleted() {
    const alert = await this.#alertCtrl.create({
      header: 'データ削除',
      message: '本デバイスに登録されているすべてのデータを削除します。',
      buttons: [
        {
          text: '閉じる',
          role: 'cancel',
        },
        {
          text: '削除',
          handler: () => {
            this.#storage.clear().then(() => this.doSignout());
          },
        },
      ],
    });
    alert.present();
  }
}
